import PropTypes from 'prop-types';

import {Box, Skeleton, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component as ContentfulComponent} from '@amblique/contentful/app/components/contentful/component';

/**
 * @override
 * IconSet component
 * - Renders collection of Icon components
 * - Flex layout with wrap and center alignment
 * - Returns null if icons array is empty
 *
 * @param {Object} props - Component props
 * @param {Contentful.IconSet} props.component - IconSet component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement|null}
 */
export const IconSet = ({component, isComplete = true, isFetching = false, ...props}) => {
  const styles = useMultiStyleConfig('contentful/icon-set', {component, isComplete, isFetching});

  const icons = component?.iconsCollection?.items || [];
  const hasIcons = icons && icons.length > 0;

  const skeletonItems = [1, 2, 3];

  if (!isComplete && isFetching) {
    return (
      <Box {...styles.root}>
        {skeletonItems.map((index) => (
          <Box key={`skeleton-${index}`} {...styles.icon}>
            <Skeleton height="64px" mb={4} width="64px" />
            <Skeleton height="16px" mb={2} width="80px" />
            <Skeleton height="14px" width="120px" />
          </Box>
        ))}
      </Box>
    );
  }

  if (!hasIcons) {
    return null;
  }

  return (
    <Box {...styles.root} {...props}>
      {icons.map((icon) => (
        <Box key={`icon-${icon.sys.id}`} {...styles.icon}>
          <ContentfulComponent component={icon} />
        </Box>
      ))}
    </Box>
  );
};

IconSet.displayName = 'IconSet';
IconSet.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
